import { Modal } from '@/components/common/dialog';
import { ModalButton } from '@/components/common/modal-button';
import { KEY_CODE_SIHEUNG } from '@/constants/app';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores/configStore';
import { toAriaLabelText } from '@/utils/tts';
import { useTranslation } from 'react-i18next';

interface UsedTTSModalProps {
    isOpen: boolean;
    onClose: () => void;
    onHomeRedirect: () => void;
    onConfirm: () => void;
}

export function UsedTTSModal({ isOpen, onClose, onHomeRedirect, onConfirm }: UsedTTSModalProps) {
    const { t } = useTranslation();
    const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode) ?? '';
    const { isHorizontal } = useOrientation();
    const isSiheungVertical = KEY_CODE_SIHEUNG.includes(storeCode) && !isHorizontal;
    const description1Key = isSiheungVertical ? 'usedTTS.description1Siheung' : 'usedTTS.description1';
    const description2Key = isSiheungVertical ? 'usedTTS.description2Siheung' : 'usedTTS.description2';
    const description1AriaLabel = toAriaLabelText(t(description1Key));
    const description2AriaLabel = toAriaLabelText(t(description2Key));
    return (
        <Modal isOpen={isOpen} onClose={onClose} className="tts-modal max-w-[calc(680*var(--vw-unit))]" closeRegionOrder={5} closeTabIndex={5}>
            <div className="flex h-full flex-col">
                <header className="text-text-main01 px-[calc(56*var(--vh-unit))] pt-[calc(56*var(--vh-unit))] pb-[calc(8*var(--vh-unit))] text-[calc(32*var(--vh-unit))] leading-normal font-bold tracking-[calc(-1.276*var(--vh-unit))]">
                    <h1
                        data-modal-region
                        tabIndex={1}
                        data-interactive
                        aria-label={t('usedTTS.title')}
                        className="focus-border text-center font-bold"
                        data-modal-region-order={1}
                    >
                        {t('usedTTS.title')}
                    </h1>
                </header>
                <main className="px-[calc(112*var(--vw-unit))]">
                    <div className="mt-[calc(40*var(--vh-unit))] mb-[calc(40*var(--vh-unit))]">
                        <div
                            className="focus-border flex flex-col gap-[calc(12*var(--vh-unit))]"
                            data-interactive
                            data-modal-region
                            data-modal-region-order={2}
                            tabIndex={2}
                            aria-label={`${t('usedTTS.subTitle1')} ${description1AriaLabel} ${t('usedTTS.subTitle2')} ${description2AriaLabel}`}
                        >
                            <div className="flex flex-col gap-[calc(12*var(--vh-unit))]">
                                <div className="text-text-main01 text-[calc(24*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-bold tracking-[calc(-0.144*var(--vh-unit))]">
                                    {t('usedTTS.subTitle1')}
                                </div>
                                <div
                                    className="text-text-main01 focus-border text-[calc(18*var(--vh-unit))] leading-[calc(24*var(--vh-unit))] font-medium tracking-[calc(-0.108*var(--vh-unit))]"
                                    dangerouslySetInnerHTML={{ __html: t(description1Key) }}
                                ></div>
                            </div>
                            <div className="flex flex-col gap-[calc(12*var(--vh-unit))]">
                                <div className="text-text-main01 focus-border text-[calc(24*var(--vh-unit))] leading-[calc(30*var(--vh-unit))] font-bold tracking-[calc(-0.114*var(--vh-unit))]">
                                    {t('usedTTS.subTitle2')}
                                </div>
                                <div
                                    className="text-text-main01 focus-border text-[calc(18*var(--vh-unit))] leading-[calc(24*var(--vh-unit))] font-medium tracking-[calc(-0.108*var(--vh-unit))]"
                                    dangerouslySetInnerHTML={{ __html: t(description2Key) }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </main>
                <footer className="flex justify-center gap-[calc(23*var(--vw-unit))] px-[calc(56*var(--vh-unit))] pt-[calc(24*var(--vh-unit))] pb-[calc(52*var(--vh-unit))]">
                    <ModalButton
                        variant="secondary"
                        role="button"
                        aria-label={t('usedTTS.buttons.cancel')}
                        onClick={onHomeRedirect}
                        data-interactive
                        type="button"
                        data-modal-region
                        data-modal-region-order={3}
                        tabIndex={3}
                    >
                        {t('usedTTS.buttons.cancel')}
                    </ModalButton>
                    <ModalButton
                        variant="primary"
                        role="button"
                        aria-label={t('usedTTS.buttons.continue')}
                        onClick={onConfirm}
                        data-interactive
                        type="button"
                        data-modal-region
                        data-modal-region-order={4}
                        tabIndex={4}
                    >
                        {t('usedTTS.buttons.continue')}
                    </ModalButton>
                </footer>
            </div>
        </Modal>
    );
}
